import React, { useState, useCallback, useRef, useEffect } from 'react';
import { MapPin, Loader2 } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { useAnalytics } from '../../../hooks/useAnalytics';

const LocationAutocomplete = ({
  id,
  inputRef,
  value = '',
  onChange,
  onSelect,
  placeholder = "Enter your city",
  className = '',
  disabled = false,
  required = false
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [suggestions, setSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const searchTimeoutRef = useRef(null);
  const containerRef = useRef(null);
  const analytics = useAnalytics();

  // Location search using backend proxy
  const searchLocation = async (query) => {
    if (!query || query.length < 3) {
      setSuggestions([]);
      return;
    }

    try {
      setIsSearching(true);

      // Use backend proxy endpoint - no API key needed in frontend
      const response = await fetch(`/api/geocoding/search?query=${encodeURIComponent(query)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        // Log detailed error information
        const errorText = await response.text();
        console.error('Location search failed:', {
          status: response.status,
          statusText: response.statusText,
          error: errorText,
          url: response.url
        });

        if (response.status === 429) {
          console.warn('Rate limit reached for location search');
        }
        throw new Error(`Search failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Location search response:', data);
      const results = data.features || [];
      console.log('Location search results:', results.length, 'results found');
      setSuggestions(results);

      if (results.length > 0) {
        setShowSuggestions(true);
      }

      // Track location search
      analytics.trackLocationSearch(query, results.length, false);
    } catch (error) {
      console.error('Location search error:', error);
      setSuggestions([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Debounced search
  const handleInputChange = useCallback((value) => {
    setInputValue(value);
    onChange?.(value);
    setSelectedIndex(-1);

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout for search
    searchTimeoutRef.current = setTimeout(() => {
      searchLocation(value);
    }, 300);
  }, [onChange]);

  // Handle suggestion selection
  const handleSelectSuggestion = (place) => {
    const locationData = {
      full_name: place.place_name,
      city: place.text,
      country: place.context?.find(c => c.id.includes('country'))?.text || '',
      region: place.context?.find(c => c.id.includes('region'))?.text || '',
      coordinates: place.center
    };

    setInputValue(place.place_name);
    onChange?.(place.place_name);
    onSelect?.(locationData);
    setShowSuggestions(false);
    setSuggestions([]);

    // Track location selection
    analytics.trackLocationSearch(inputValue, suggestions.length, true);
  };

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && suggestions[selectedIndex]) {
          handleSelectSuggestion(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        break;
      default:
        break;
    }
  };

  // Click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Clean up search timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Format suggestion display
  const formatSuggestion = (place) => {
    const parts = place.place_name.split(', ');
    return {
      primary: parts[0],
      secondary: parts.slice(1).join(', ')
    };
  };

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      <div className="relative">
        <div className="absolute left-5 top-1/2 -translate-y-1/2 z-10 pointer-events-none">
          {isSearching ? (
            <Loader2 className="h-4 w-4 animate-spin text-white/40" />
          ) : (
            <MapPin className="h-4 w-4 text-white/40" />
          )}
        </div>
        <input
          ref={inputRef}
          id={id}
          type="text"
          value={inputValue}
          onChange={(e) => handleInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          className={cn(
            "form-input",
            "!pl-12 pr-4",
            "h-[48px]",
            disabled && "opacity-50 cursor-not-allowed"
          )}
          autoComplete="off"
        />
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <div className="survey-suggestions w-full mt-2 bg-black/90 border border-white/20 rounded-xl shadow-lg">
          <div className="max-h-[180px] overflow-y-auto custom-scrollbar">
            {suggestions.map((place, index) => {
              const { primary, secondary } = formatSuggestion(place);
              return (
                <button
                  key={place.id}
                  type="button"
                  onClick={() => handleSelectSuggestion(place)}
                  onMouseEnter={() => setSelectedIndex(index)}
                  className={cn(
                    "w-full px-3 py-2 text-left hover:bg-white/10 transition-colors",
                    "flex items-center gap-2",
                    selectedIndex === index && "bg-white/10"
                  )}
                >
                  <MapPin className="h-4 w-4 text-white/40 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate text-white">{primary}</div>
                    {secondary && (
                      <div className="text-xs text-white/60 truncate">
                        {secondary}
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationAutocomplete;
